'use client';

import React, { useState, useTransition, useRef, useEffect, useCallback } from 'react';
import { apiHomeBrandedProducts } from '@/apis/user';
import { getBaseImageUrl } from '@/constants/hooks';
import { BrandedProduct } from '@/services/dataTypes';
import BrandedProductCard from './BrandedProductCard';

const VISIBLE = 4;
const GAP = 24; // px — matches gap-6

interface PaginationInfo {
  total: number;
  per_page: number;
  current_page: number;
  last_page: number;
  from?: number;
  to?: number;
}

interface BrandedProductsCarouselProps {
  companyId: string;
  mer_slug_type: string;
  domain: string;
  initialProducts: BrandedProduct[];
  initialPagination: PaginationInfo;
}

const BrandedProductsCarousel = ({
  companyId,
  mer_slug_type,
  domain,
  initialProducts,
  initialPagination,
}: BrandedProductsCarouselProps) => {
  const [products, setProducts] = useState<BrandedProduct[]>(initialProducts);
  const [pagination, setPagination] = useState<PaginationInfo>(initialPagination);
  const [currentPage, setCurrentPage] = useState<number>(initialPagination.current_page);
  const [isPending, startTransition] = useTransition();

  // Dual-track slider: current visible + incoming (off-screen, pre-rendered)
  const [nextProducts, setNextProducts] = useState<BrandedProduct[] | null>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isAnimating = useRef(false);

  const vw = useCallback(() => viewportRef.current?.clientWidth ?? 0, []);

  // Keep current track centred after resize
  useEffect(() => {
    const onResize = () => {
      if (wrapperRef.current) { wrapperRef.current.style.transition = 'none'; wrapperRef.current.style.transform = 'translateX(0)'; }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const navigate = (dir: 'left' | 'right') => {
    if (isPending || isAnimating.current) return;
    isAnimating.current = true;

    const nextPage =
      dir === 'right'
        ? currentPage < pagination.last_page ? currentPage + 1 : 1
        : currentPage > 1 ? currentPage - 1 : pagination.last_page;

    startTransition(async () => {
      try {
        const res = await apiHomeBrandedProducts(companyId, nextPage, pagination.per_page);
        if (!res.data?.products) { isAnimating.current = false; return; }

        // Stage the new cards — React renders them into the incoming slot
        setNextProducts(res.data.products);

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const W = vw();
            const wrapper = wrapperRef.current;
            if (!wrapper) { isAnimating.current = false; return; }

            // Place wrapper so incoming track (at position W) is off-screen right (or left)
            const startX = dir === 'right' ? 0 : -W;
            wrapper.style.transition = 'none';
            wrapper.style.transform = `translateX(${startX}px)`;

            // Force reflow
            wrapper.getBoundingClientRect();

            // Slide the whole wrapper so incoming lands at 0
            const endX = dir === 'right' ? -W : 0;
            wrapper.style.transition = 'transform 380ms cubic-bezier(0.4, 0, 0.2, 1)';
            wrapper.style.transform = `translateX(${endX}px)`;

            setTimeout(() => {
              setProducts(res.data!.products!);
              if (res.data!.pagination) setPagination(res.data!.pagination);
              setCurrentPage(nextPage);
              setNextProducts(null);
              // Reset wrapper silently
              if (wrapper) { wrapper.style.transition = 'none'; wrapper.style.transform = 'translateX(0)'; }
              isAnimating.current = false;
            }, 390);
          });
        });
      } catch (e) {
        console.error('Error fetching products:', e);
        isAnimating.current = false;
      }
    });
  };

  if (!products || products.length === 0) return null;

  const renderCards = (list: BrandedProduct[]) =>
    list.map((product, index) => (
      <BrandedProductCard
        key={product.id || index}
        product={product}
        domain={domain}
        imageSrc={getBaseImageUrl(domain, product?.product_image, '')}
        merchant={product?.merchant}
        mer_slug_type={mer_slug_type}
      />
    ));

  return (
    <div className="relative">
      {/* Left chevron */}
      <button
        onClick={() => navigate('left')}
        aria-label="Previous"
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white shadow-lg hover:shadow-xl transition-all duration-150 flex items-center justify-center group hover:bg-[#F97316] cursor-pointer"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"
          className="w-5 h-5 md:w-6 md:h-6 text-[#1E3A5F] group-hover:text-white transition-colors">
          <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
        </svg>
      </button>

      {/* Viewport: overflow-hidden clips both tracks; py/-my lets hover shadow breathe */}
      <div ref={viewportRef} className="relative overflow-hidden py-3 -my-3">

        {/* Outer wrapper holds both tracks side-by-side in a flex row so
            the incoming track is always at the exact same vertical position */}
        <div ref={wrapperRef} className="flex" style={{ willChange: 'transform' }}>

          {/* Current track */}
          <div
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 flex-none w-full"
          >
            {renderCards(products)}
          </div>

          {/* Incoming track — only mounted during transition */}
          {nextProducts && (
            <div
              className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 flex-none w-full"
            >
              {renderCards(nextProducts)}
            </div>
          )}
        </div>
      </div>

      {/* Right chevron */}
      <button
        onClick={() => navigate('right')}
        aria-label="Next"
        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white shadow-lg hover:shadow-xl transition-all duration-150 flex items-center justify-center group hover:bg-[#F97316] cursor-pointer"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"
          className="w-5 h-5 md:w-6 md:h-6 text-[#1E3A5F] group-hover:text-white transition-colors">
          <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
        </svg>
      </button>
    </div>
  );
};

export default BrandedProductsCarousel;
